import { Alert, Anchor, Button, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { passwordSchema } from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import { resetPassword } from './api';
import { AuthLayout } from './AuthLayout';

/**
 * Смена пароля по ссылке из письма.
 *
 * Токен приходит фрагментом адреса — после решётки, — и это не мелочь
 * оформления. Всё, что стоит до решётки, попадает в журнал веб-сервера при
 * каждом обращении; фрагмент браузер серверу не отправляет вовсе. Ключ от
 * чужого аккаунта в access-log остаться не должен.
 *
 * Прочитав токен, страница сразу стирает его из адресной строки. Иначе он
 * останется в истории браузера, в открытой вкладке за спиной и в ссылке,
 * которую человек может скопировать кому-то ещё. Дальше токен живёт только в
 * памяти страницы и уходит на сервер телом запроса.
 */

/** Только пароль: токен формой не редактируется и в её значениях не лежит. */
const formSchema = z.object({ password: passwordSchema });
type FormValues = z.infer<typeof formSchema>;

/** Достаёт токен из фрагмента и тут же очищает адрес. */
function takeTokenFromUrl(): string {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  const token = new URLSearchParams(raw).get('token') ?? '';

  if (token) {
    /*
      replaceState, а не переход: адрес без токена должен встать на место
      прежнего, не оставив записи в истории. Кнопка «назад» не должна
      возвращать ссылку с ключом.
    */
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
  return token;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();

  /*
    Токен читается один раз, при первом рендере: к следующему адресная строка
    уже очищена, и повторное чтение вернуло бы пустоту.
  */
  const [token] = useState(takeTokenFromUrl);

  const form = useForm<FormValues>({
    initialValues: { password: '' },
    validate: zodResolver(formSchema),
    validateInputOnBlur: true,
  });

  const change = useMutation({
    mutationFn: (values: FormValues) => resetPassword({ token, password: values.password }),
  });

  /* После успеха — на вход: все сессии отозваны, входить надо заново. */
  useEffect(() => {
    if (!change.isSuccess) return;
    const timer = window.setTimeout(() => navigate('/login', { replace: true }), 2500);
    return () => window.clearTimeout(timer);
  }, [change.isSuccess, navigate]);

  if (!token) {
    return (
      <AuthLayout title="Ссылка не открылась" subtitle="В адресе нет ключа восстановления.">
        <Stack gap="md">
          <Alert color="red" icon={<IconAlertCircle size={17} />} title="Ссылка неполная">
            Похоже, адрес скопировался не целиком. Откройте ссылку из письма
            целиком или запросите новую.
          </Alert>
          <Button variant="light" component={Link} to="/forgot-password">
            Запросить новую ссылку
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  if (change.isSuccess) {
    return (
      <AuthLayout title="Пароль изменён" subtitle="Войдите с новым паролем.">
        <Stack gap="md">
          <Alert color="teal" icon={<IconCheck size={17} />} title="Готово">
            Пароль изменён. На всякий случай мы завершили все сеансы — если в
            аккаунт заходил кто-то ещё, теперь он вышел.
          </Alert>
          <Button component={Link} to="/login">
            Войти
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Новый пароль" subtitle="Придумайте пароль, которым будете входить.">
      <form onSubmit={form.onSubmit((values) => change.mutate(values))} noValidate>
        <Stack gap="md">
          {change.isError ? (
            <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не удалось сменить пароль">
              {toUserMessage(change.error)}
            </Alert>
          ) : null}

          <PasswordInput
            label="Новый пароль"
            placeholder="Не короче 8 символов"
            autoComplete="new-password"
            required
            {...form.getInputProps('password')}
          />

          <Button type="submit" loading={change.isPending} fullWidth>
            Сменить пароль
          </Button>
        </Stack>
      </form>

      <Text size="sm" c="var(--text-muted)" ta="center" mt="md">
        Ссылка устарела?{' '}
        <Anchor component={Link} to="/forgot-password">
          Запросить новую
        </Anchor>
      </Text>
    </AuthLayout>
  );
}
